/**
 * Design tokens for the VMS dashboard.
 *
 * Aligned to the Felix app's brand: Inter, lime (#C7FF00) as the single accent,
 * on a dark shell. Before this the file carried a green primary and a purple
 * secondary while the lime appeared hardcoded in 25 places, so three accents
 * competed; and `colors.bg` was read by GlobalStyle but never defined, which is
 * why the shell had no background at all outside the login page.
 *
 * The original key names are kept so existing styled-components keep working —
 * they now point at the new palette.
 */

// --- palette ---------------------------------------------------------------
const lime = "#C7FF00";
const limeDim = "#A8D900";

const colors = {
  // Brand
  primary: lime,
  primaryHover: limeDim,
  // Kept for components that still reference it; now a neutral accent rather
  // than the old purple, so nothing reads as a second brand colour.
  secondary: "#2A3138",

  // Surfaces, darkest to lightest
  bg: "#14181A",
  surface: "#1C2126",
  surfaceRaised: "#232A30",
  surfaceHover: "#2A323A",

  // Text
  text: "#F5F7F8",
  textMuted: "#8A9199",
  textFaint: "#5C656E",

  // Lines
  border: "#2C343B",
  borderStrong: "#3A444D",

  // Status
  danger: "#FF6972",
  dangerHover: "#FF8A91",
  success: lime,
  warning: "#FFB020",
  info: "#19E8E0",

  // Text that sits on the lime accent
  onPrimary: "#0F172A",

  // Legacy aliases, remapped onto the ramp above
  black: "#0F172A",
  white: "#FFFFFF",
  red: "#FF6972",
  lightGray: "#232A30",
  gray: "#2C343B",
};

const theme = {
  colors,

  fonts: {
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"Akkurat-Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  },

  // Index-addressed, as the existing components expect (theme.fontSizes[3]).
  fontSizes: [12, 14, 16, 18, 20, 22, 24, 32, 50, 64],
  space: [0, 4, 8, 16, 32, 64, 128, 256],
  fontWeights: { body: 400, medium: 500, heading: 700, bold: 700 },

  radii: { sm: 8, md: 12, lg: 16, xl: 20, pill: 999 },

  shadows: {
    sm: "0 1px 2px rgba(0,0,0,0.30)",
    md: "0 4px 16px rgba(0,0,0,0.35)",
    lg: "0 12px 32px rgba(0,0,0,0.45)",
    focus: `0 0 0 3px rgba(199, 255, 0, 0.30)`,
  },

  transitions: { fast: "120ms ease", base: "200ms ease" },
};

export default theme;
